import type {
  IOrigin,
  TDestin,
  IOriginSearchData,
  IDestinSearchData,
  TFilteredOrigDestResult,
  TCountriesList
} from '@/types/OrigDest'
import { CATEGORIES, DESTIN, ORIGIN } from '@/utils/constants/origDest'
import type { useOrigDestData } from '@/composables/orig-dest/useOrigDestData'
import highlight from '@/utils/highlight'
import { normalizeForSearch } from '@/utils/origDestSearch'
import { TYPE_AIRPORT } from '@/utils/constants/fsf'
import { sortByLocaleCompare } from '@/utils/localeCompare'

export const useOrigDestSearch = (
  origDestData: ReturnType<typeof useOrigDestData>,
  options?: { destinCode?: () => string | undefined }
) => {
  const { t } = useI18n()

  const searchTerm = ref('')
  const openedOrigDest = ref<typeof ORIGIN | typeof DESTIN>(ORIGIN)

  const normalizedSearchTerm = computed(() => normalizeForSearch(searchTerm.value))

  const nearbyOrigin = computed<IOrigin | null>(() => {
    const nearest = origDestData.nearestAirport.value
    const origins = origDestData.allOrigins.value

    if (!nearest || !origins) {
      return null
    }

    return Object.values(origins).find((o) => o.code === nearest.code) || null
  })

  const destinKey = computed(() => {
    const code = options?.destinCode?.()?.trim().toUpperCase()
    return code ? `${code}${TYPE_AIRPORT}` : null
  })

  const hasRouteToDestination = (origin: IOrigin): boolean => {
    if (!destinKey.value) return false
    return !!origDestData.allOriginDestinations(`${origin.code}${origin.type}`)[destinKey.value]
  }

  const filteredBtOrigins = computed(() => {
    const origins = origDestData.origins.value.btOrigins
    if (!destinKey.value) return origins
    return origins.filter(hasRouteToDestination)
  })

  const filteredNonBtOrigins = computed(() => {
    const origins = origDestData.origins.value.nonBtOrigins
    if (!destinKey.value) return origins
    return origins.filter(hasRouteToDestination)
  })

  const filteredNearbyOrigin = computed<IOrigin | null>(() => {
    return destinKey.value ? null : nearbyOrigin.value
  })

  const filteredOriginCountries = computed<TCountriesList[]>(() => {
    if (!destinKey.value) return origDestData.originCountries.value

    const seen = new Set<string>()
    const countries: TCountriesList[] = []

    Object.values(origDestData.allOrigins.value)
      .filter(hasRouteToDestination)
      .forEach(({ country, countryCode }) => {
        if (!seen.has(countryCode)) {
          seen.add(countryCode)
          countries.push({ value: country, key: countryCode })
        }
      })

    return sortByLocaleCompare(countries, 'value')
  })

  watch(
    () => ({
      countries: filteredOriginCountries.value,
      selectedCategory: origDestData.selectedCategory.value.origin,
      selectedSubCategory: origDestData.selectedSubCategory.value.origin
    }),
    ({ countries, selectedCategory, selectedSubCategory }) => {
      if (!destinKey.value || selectedCategory !== CATEGORIES.COUNTRIES) return

      const selectedCountry = selectedSubCategory
      if (!selectedCountry) return

      const isSelectedCountryAvailable = countries.some(({ key }) => key === selectedCountry)

      if (isSelectedCountryAvailable) return

      origDestData.selectedCategory.value.origin = CATEGORIES.ALL
      origDestData.selectedSubCategory.value.origin = ''
    },
    { immediate: true }
  )

  // Separate the string into parts that are inside parentheses and parts that are not i.e. London (Gatwick) => ['Gatwick', 'London']
  const extractAptParts = (apt: string) => {
    const PARENTHESIS_AND_TEXT_EXTRACTOR = /\(([^)]+)\)|([^(),]+)/g
    const matches = Array.from(apt.matchAll(PARENTHESIS_AND_TEXT_EXTRACTOR))

    return matches.map((match) => match[1] || match[2])
  }

  const prepareSearchData = <T extends IOrigin | TDestin>(origDest: T[]) => {
    return origDest?.map((item) => {
      const normalizedCity = normalizeForSearch(item.city)
      const normalizedCode = normalizeForSearch(item.code)
      const normalizedCountry = normalizeForSearch(item.country)
      const aptParts = extractAptParts(normalizeForSearch(item.apt))
      const searchAliases = item.searchAliases?.flatMap((alias) => extractAptParts(alias)) ?? []

      return {
        ...item,
        searchMeta: [...new Set([normalizedCity, normalizedCode, normalizedCountry, ...aptParts, ...searchAliases])]
      }
    })
  }

  const filterOrigDest = <T extends IOriginSearchData | IDestinSearchData>(origDest: T[]) => {
    return origDest?.filter((item) => {
      if (!item) return false

      return item.searchMeta.some((field) => field.startsWith(normalizedSearchTerm.value))
    })
  }

  const preparedDestinSearch = computed(() => {
    return {
      btData: prepareSearchData(origDestData.destinations.value.btDestinations),
      nonBtData: prepareSearchData(origDestData.destinations.value.nonBtDestinations)
    }
  })

  const preparedOriginSearch = computed(() => {
    return {
      btData: prepareSearchData(filteredBtOrigins.value),
      nonBtData: prepareSearchData(filteredNonBtOrigins.value)
    }
  })

  const isOriginSelect = computed(() => openedOrigDest.value === ORIGIN)

  const isCategoryAll = computed(() => origDestData.selectedCategory.value?.[openedOrigDest.value] === CATEGORIES.ALL)
  const isDirectFlightsCategory = computed(() => {
    return openedOrigDest.value === DESTIN && origDestData.selectedCategory.value.destin === CATEGORIES.DIRECT
  })

  const addDefaultResults = (
    result: TFilteredOrigDestResult[],
    btData: IOrigin[] | TDestin[],
    nonBtData: IOrigin[] | TDestin[],
    nearby: IOrigin | null
  ) => {
    if (!isCategoryAll.value) {
      result.push({ title: t('origDest.airports') })
    }

    if (isCategoryAll.value && isOriginSelect.value && nearby && Object.keys(nearby).length) {
      result.push({ title: t('origDest.nearbyAirports') }, nearby)
    }

    const btTitle = t(`origDest.${openedOrigDest.value}.ownFlights`)
    const nonBtTitle = t(`origDest.${openedOrigDest.value}.connectedFlights`)

    result.push(...(isCategoryAll.value && btData.length ? [{ title: btTitle }] : []), ...btData)
    result.push(...(isCategoryAll.value && nonBtData.length ? [{ title: nonBtTitle }] : []), ...nonBtData)

    return result
  }

  const addDirectFlightResults = (result: TFilteredOrigDestResult[]) => {
    if (origDestData.directDestinations.value.length) {
      result.push({ title: t('origDest.destin.directFlights') }, ...origDestData.directDestinations.value)
    }

    if (origDestData.connectingDestinations.value.length) {
      result.push(
        { title: t(`origDest.${openedOrigDest.value}.connectedFlights`) },
        ...origDestData.connectingDestinations.value
      )
    }

    return result
  }

  const filteredOrigDestData = computed(() => {
    const result: TFilteredOrigDestResult[] = []

    const btData = isOriginSelect.value ? filteredBtOrigins.value : origDestData.destinations.value.btDestinations
    const nonBtData = isOriginSelect.value
      ? filteredNonBtOrigins.value
      : origDestData.destinations.value.nonBtDestinations
    const nearby = isOriginSelect.value ? filteredNearbyOrigin.value : null

    if (!searchTerm.value) {
      if (isDirectFlightsCategory.value) {
        return addDirectFlightResults(result)
      }

      return addDefaultResults(result, btData, nonBtData, nearby)
    }

    const searchBtData = isOriginSelect.value ? preparedOriginSearch.value.btData : preparedDestinSearch.value.btData
    const searchNonBtData = isOriginSelect.value
      ? preparedOriginSearch.value.nonBtData
      : preparedDestinSearch.value.nonBtData

    const getHighlightedApt = (apt: string) => {
      return extractAptParts(apt)
        .map((aptPart) => {
          const highlightedPart = highlight(aptPart, normalizedSearchTerm.value)
          return apt.includes(`(${aptPart})`) ? `(${highlightedPart})` : highlightedPart
        })
        .join(' ')
    }

    const filteredData = filterOrigDest([...searchBtData, ...searchNonBtData]).map((item) => {
      const highlightedApt = getHighlightedApt(item.apt)

      return {
        ...item,
        city: highlight(item.city, normalizedSearchTerm.value),
        code: highlight(item.code, normalizedSearchTerm.value),
        country: highlight(item.country, normalizedSearchTerm.value),
        apt: highlightedApt
      }
    })

    if (filteredData.length) {
      result.push({ title: t('origDest.search.results') }, ...filteredData)
    } else {
      result.push({ title: t('origDest.search.noResults') })
    }

    return result
  })

  watch(
    () => searchTerm.value,
    () => {
      origDestData.hasSearchTerm.value = !!searchTerm.value
    }
  )

  return { searchTerm, openedOrigDest, filteredOrigDestData, filteredOriginCountries }
}
